import React from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import PublicHeader from "./PublicHeader";
import DiscoveryShelf from "./DiscoveryShelf";
import { genreColor } from "../../utils/genreColor";
import shopPreview from "../../assets/AuthorShopPic.jpg";

export const discoveryGenres = ["Fantasy", "Romance", "Romantasy", "Science Fiction", "Horror", "Thriller / Suspense", "Western"];
export function GenreLinks({ circles = false }) {
  return <div className={`genre-links${circles ? " genre-circle-links" : ""}`}>
    {discoveryGenres.map(genre => <Link key={genre} aria-label={genre} to={"/market?genre=" + encodeURIComponent(genre)} style={circles ? { "--genre-color": genreColor(genre) } : undefined}>
      {circles ? <span className="genre-circle">
        {genre === "Science Fiction" ? <><span>Science</span><span>Fiction</span></> :
          genre === "Thriller / Suspense" ? <><span>Thriller /</span><span>Suspense</span></> : genre}
      </span> : genre}
      {!circles && <span aria-hidden="true">↗</span>}
    </Link>)}
  </div>;
}

export default function LandingPage() {
  return <div className="public-design landing-story"><PublicHeader /><main>
    <section className="public-hero public-container">
      <div>
        <p className="public-eyebrow landing-chapter">Prologue · Welcome to the Bookish Circle</p>
        <h1>Every bookish adventure<br /><em>begins with a story.</em></h1>
        <p className="public-lead">The book you stay up too late to finish. The character you can’t stop thinking about. The author you wish you’d found sooner. Find your next favorite among the independent voices of the Bookish Circle.</p>
        <div className="public-actions">
          <Link className="public-button" to="/market">Browse books →</Link>
          <Link className="public-button-secondary" to="/signupreader">Create a reader account →</Link>
          <Link className="public-text-link" to="/test">Test →</Link>
        </div>
        <p className="public-note">Join free as a Circle Reader to save your next reads, follow your favorites, and enjoy Reader Appreciation discounts from participating authors.</p>
      </div>
      <DiscoveryShelf />
    </section>

    <section className="public-section public-container landing-genres">
      <p className="public-eyebrow landing-chapter">Chapter One · Follow your curiosity</p>
      <h2>What are you in the mood for?</h2>
      <p>A little magic? A slow-burn romance? One more chapter with the lights on? Step into a genre you love.</p>
      <GenreLinks circles />
    </section>

    <section className="public-section public-soft"><div className="public-container">
      <p className="public-eyebrow landing-chapter">Chapter Two · Find your people</p>
      <h2>The story doesn’t end on the last page.</h2>
      <p className="public-lead">Keep a place for the books you want to read and the authors you want to hear from. Your Circle brings them together.</p>
      <div className="public-three-up">
        {[
          ["01", "Discover a new world", "Wander the shelves, find an independent voice, and meet the person behind your next favorite story."],
          ["02", "Save your next read", "Build your to-be-read list. There’s always room for one more book you can’t wait to open."],
          ["03", "Keep the story going", "Follow authors and genre circles, and find author newsletters in one place. Stay close to the stories you love."],
        ].map(([number, title, text]) => <article key={number}><span className="public-step">{number}</span><h3>{title}</h3><p>{text}</p></article>)}
      </div>
      <Link className="public-text-link" to="/signupreader">Create your free reader account →</Link>
    </div></section>

    <section className="public-section public-container"><div className="landing-appreciation">
      <div>
        <BookOpen size={36} strokeWidth={1.4} aria-hidden="true" />
        <p className="public-eyebrow landing-chapter">Chapter Three · A thank-you from the authors</p>
        <h2>A little appreciation.<br />For your next chapter.</h2>
        <p>As a Circle Reader, you can take part in the <strong>Reader Appreciation Program</strong>. Participating authors offer special discounts when you’re signed in and shop directly from their Bookish Circle stores.</p>
        <Link className="public-button" to="/signupreader">Become a Circle Reader →</Link>
      </div>
      <div className="landing-reader-perks">
        <div><span className="landing-perk-amount">$1 off</span><h3>Each eligible ebook</h3><p>On ebooks priced $3 or more from participating authors.</p></div>
        <div><span className="landing-perk-amount">10% off</span><h3>Physical books &amp; bookish finds</h3><p>On physical products from participating authors, including bookish merchandise.</p></div>
        <p className="public-note">A free reader account is all you need to join. Sign in to receive eligible discounts. Shipping stays the same.</p>
      </div>
    </div></section>

    <section className="public-section public-container public-split">
      <div className="public-preview"><img src={shopPreview} alt="Example Bookish Circle author storefront" loading="lazy" /></div>
      <div>
        <p className="public-eyebrow landing-chapter">Behind the stories</p>
        <h2>Every book begins<br />with someone who imagined it.</h2>
        <p className="public-lead">Meet independent authors in their own corner of the Circle. Explore their books, read their updates, and discover what they’re writing next.</p>
        <Link className="public-text-link" to="/authors">Writing your own story? Explore author membership →</Link>
      </div>
    </section>

    <section className="public-section public-container public-closing">
      <p className="public-eyebrow landing-chapter">Your next chapter</p>
      <h2>There’s a place for you in the Circle.</h2>
      <p>Bring your curiosity. Leave with a longer reading list.</p>
      <div className="public-actions landing-closing-actions">
        <Link className="public-button" to="/signupreader">Create your free reader account →</Link>
        <Link className="public-text-link" to="/about">Our story →</Link>
      </div>
    </section>
  </main></div>;
}
